import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { 
  Clock, CheckCircle2, AlertTriangle, RefreshCw, User, Building2, 
  Calendar, Briefcase, AlertCircle, Loader2, Timer
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { formatDate } from '../utils/dateFormat';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUTS_ESSAI = {
  urgente: { label: 'Urgent', color: 'bg-red-100 text-red-700 border-red-200', icon: AlertTriangle },
  proche: { label: 'Bientôt', color: 'bg-orange-100 text-orange-700 border-orange-200', icon: Timer },
  en_cours: { label: 'En cours', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Clock },
  expiree: { label: 'Expirée', color: 'bg-slate-100 text-slate-700 border-slate-200', icon: AlertCircle }
};

const PeriodesEssaiManager = () => {
  const [periodes, setPeriodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRenouvellerDialog, setShowRenouvellerDialog] = useState(false);
  const [selectedContrat, setSelectedContrat] = useState(null);
  const [dureeRenouvellement, setDureeRenouvellement] = useState(30);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadPeriodes();
  }, []);

  const loadPeriodes = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/contrats/periodes-essai`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPeriodes(response.data);
    } catch (error) {
      console.error('Erreur chargement périodes:', error);
      toast.error('Erreur lors du chargement des périodes d\'essai');
    } finally {
      setLoading(false);
    }
  };

  const handleValider = async (contratId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/contrats/${contratId}/valider-essai`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Période d\'essai validée avec succès');
      loadPeriodes();
    } catch (error) {
      console.error('Erreur validation:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
    }
  };

  const handleRenouveler = async () => {
    if (!selectedContrat) return;
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/api/contrats/${selectedContrat.id}/renouveler-essai?duree_jours=${dureeRenouvellement}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Période d'essai renouvelée jusqu'au ${response.data.nouvelle_fin}`);
      setShowRenouvellerDialog(false);
      setSelectedContrat(null);
      loadPeriodes();
    } catch (error) {
      console.error('Erreur renouvellement:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors du renouvellement');
    } finally {
      setSubmitting(false);
    }
  };

  const openRenouvellerDialog = (contrat) => {
    setSelectedContrat(contrat);
    setDureeRenouvellement(30);
    setShowRenouvellerDialog(true);
  };

  const getJoursRestantsLabel = (jours) => {
    if (jours === null || jours === undefined) return '';
    if (jours < 0) return `${Math.abs(jours)} jour(s) de retard`;
    if (jours === 0) return "Aujourd'hui";
    if (jours === 1) return "Demain";
    return `${jours} jours restants`;
  };

  // Grouper par statut
  const groupedPeriodes = {
    urgente: periodes.filter(p => p.statut_essai === 'urgente'),
    proche: periodes.filter(p => p.statut_essai === 'proche'),
    en_cours: periodes.filter(p => p.statut_essai === 'en_cours'),
    expiree: periodes.filter(p => p.statut_essai === 'expiree')
  };

  if (loading) {
    return (
      <Card className="rounded-3xl">
        <CardContent className="p-8 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="rounded-3xl">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Timer className="h-5 w-5 text-orange-500" />
                Suivi des Périodes d'Essai
              </CardTitle>
              <CardDescription>
                {periodes.length} période(s) d'essai en cours ou à surveiller
              </CardDescription>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={loadPeriodes}
              className="gap-2"
              data-testid="refresh-periodes-btn"
            >
              <RefreshCw className="h-4 w-4" />
              Actualiser
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Statistiques rapides */}
          <div className="grid grid-cols-4 gap-4 mb-6">
            <div className="bg-red-50 rounded-xl p-4 text-center border border-red-100">
              <div className="text-2xl font-bold text-red-600">{groupedPeriodes.urgente.length}</div>
              <div className="text-sm text-red-600">Urgentes (&lt;7j)</div>
            </div>
            <div className="bg-orange-50 rounded-xl p-4 text-center border border-orange-100">
              <div className="text-2xl font-bold text-orange-600">{groupedPeriodes.proche.length}</div>
              <div className="text-sm text-orange-600">Proches (&lt;14j)</div>
            </div>
            <div className="bg-blue-50 rounded-xl p-4 text-center border border-blue-100">
              <div className="text-2xl font-bold text-blue-600">{groupedPeriodes.en_cours.length}</div>
              <div className="text-sm text-blue-600">En cours</div>
            </div>
            <div className="bg-slate-50 rounded-xl p-4 text-center border border-slate-200">
              <div className="text-2xl font-bold text-slate-600">{groupedPeriodes.expiree.length}</div>
              <div className="text-sm text-slate-600">Expirées</div>
            </div>
          </div>

          {periodes.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Clock className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <p>Aucune période d'essai en cours</p>
              <p className="text-sm mt-1">Les périodes d'essai apparaîtront ici lors de la création de contrats</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Afficher les urgentes en premier */}
              {['urgente', 'proche', 'en_cours', 'expiree'].map(statut => {
                const items = groupedPeriodes[statut];
                if (items.length === 0) return null;
                
                const StatusConfig = STATUTS_ESSAI[statut];
                const StatusIcon = StatusConfig.icon;
                
                return (
                  <div key={statut}>
                    <h4 className="text-sm font-medium text-slate-500 mb-2 flex items-center gap-2">
                      <StatusIcon className="h-4 w-4" />
                      {StatusConfig.label} ({items.length})
                    </h4>
                    <div className="grid gap-3">
                      {items.map((periode) => (
                        <Card 
                          key={periode.id} 
                          className={`border-l-4 ${statut === 'urgente' ? 'border-l-red-500' : statut === 'proche' ? 'border-l-orange-500' : statut === 'expiree' ? 'border-l-slate-400' : 'border-l-blue-500'}`}
                          data-testid={`periode-card-${periode.id}`}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  <User className="h-4 w-4 text-slate-400" />
                                  <span className="font-medium">{periode.beneficiaire_nom}</span>
                                  <Badge className={StatusConfig.color}>
                                    {getJoursRestantsLabel(periode.jours_restants)}
                                  </Badge>
                                  {periode.periode_essai_renouvelee && (
                                    <Badge variant="outline" className="text-purple-600 border-purple-300">
                                      Renouvelée
                                    </Badge>
                                  )}
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm text-slate-600">
                                  <div className="flex items-center gap-1">
                                    <Building2 className="h-3.5 w-3.5 text-slate-400" />
                                    {periode.entreprise_nom}
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <Briefcase className="h-3.5 w-3.5 text-slate-400" />
                                    {periode.poste || 'Non spécifié'}
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <Calendar className="h-3.5 w-3.5 text-slate-400" />
                                    Début: {formatDate(periode.date_debut)}
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <Timer className="h-3.5 w-3.5 text-slate-400" />
                                    Fin essai: {formatDate(periode.periode_essai_fin)}
                                  </div>
                                </div>
                              </div>
                              <div className="flex gap-2 ml-4">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openRenouvellerDialog(periode)}
                                  className="gap-1"
                                  data-testid={`renouveler-btn-${periode.id}`}
                                >
                                  <RefreshCw className="h-3.5 w-3.5" />
                                  Renouveler
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => handleValider(periode.id)}
                                  className="gap-1 bg-emerald-600 hover:bg-emerald-700"
                                  data-testid={`valider-btn-${periode.id}`}
                                >
                                  <CheckCircle2 className="h-3.5 w-3.5" />
                                  Valider
                                </Button>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog de renouvellement */}
      <Dialog open={showRenouvellerDialog} onOpenChange={setShowRenouvellerDialog}>
        <DialogContent className="sm:max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-blue-600" />
              Renouveler la période d'essai
            </DialogTitle>
            <DialogDescription>
              {selectedContrat && (
                <>
                  Renouveler la période d'essai de <strong>{selectedContrat.beneficiaire_nom}</strong> 
                  {' '}chez <strong>{selectedContrat.entreprise_nom}</strong>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Durée de renouvellement (jours)</Label>
              <Input
                type="number"
                value={dureeRenouvellement}
                onChange={(e) => setDureeRenouvellement(parseInt(e.target.value) || 0)}
                min={1}
                max={180}
                className="mt-1"
                data-testid="duree-renouvellement-input"
              />
              <p className="text-sm text-slate-500 mt-1">
                Durées courantes : 30 jours (1 mois), 60 jours (2 mois)
              </p>
            </div>
            
            {selectedContrat && (
              <div className="bg-blue-50 p-3 rounded-lg text-sm">
                <p className="text-blue-700">
                  <strong>Fin actuelle :</strong> {formatDate(selectedContrat.periode_essai_fin)}
                </p>
                <p className="text-blue-700 mt-1">
                  <strong>Nouvelle fin :</strong> {(() => {
                    try {
                      const fin = new Date(selectedContrat.periode_essai_fin);
                      fin.setDate(fin.getDate() + dureeRenouvellement);
                      return formatDate(fin.toISOString().split('T')[0]);
                    } catch {
                      return '-';
                    }
                  })()}
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRenouvellerDialog(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleRenouveler} 
              disabled={submitting || dureeRenouvellement < 1}
              className="gap-2"
              data-testid="confirm-renouvellement-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Renouvellement...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4" />
                  Confirmer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PeriodesEssaiManager;
